import { Button, Card, CardContent, EmptyState, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ShieldOff } from "lucide-react";
import { hasPermission } from "../../features/ops/permissions.ts";
import {
  useReportAction,
  useReports,
  useUnbanUser,
  useVenueBans,
} from "../../features/ops/queries.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";

export const Route = createFileRoute("/_app/moderation")({ component: ModerationScreen });

const severityTone = {
  low: "neutral",
  medium: "info",
  high: "warning",
  critical: "danger",
} as const;

function ModerationScreen() {
  const navigate = useNavigate();
  const principal = getStaffPrincipal();
  const canAction = hasPermission(principal, "moderation:action");
  const { data: reports = [] } = useReports();
  const { data: bans = [] } = useVenueBans();
  const action = useReportAction();
  const unban = useUnbanUser();
  return (
    <div className="space-y-6">
      <PageHeader
        title="Moderation / ความปลอดภัย"
        subtitle="Reports are sorted by severity and recency. Reporter identity is never disclosed to reported parties."
      />
      <div className="grid gap-4 lg:grid-cols-2">
        {reports.map((report) => (
          <Card key={report.id}>
            <CardContent className="space-y-3 p-5">
              <button
                type="button"
                className="block text-left"
                onClick={() =>
                  navigate({ to: "/moderation/$reportId", params: { reportId: report.id } })
                }
              >
                <p className="font-display font-semibold text-lg">{report.reason}</p>
                <p className="text-muted-foreground text-sm">
                  {report.targetLabel} · {report.submittedAt}
                </p>
              </button>
              <div className="flex flex-wrap gap-2">
                <StatusChip tone={severityTone[report.severity]}>{report.severity}</StatusChip>
                <StatusChip tone="info">{report.status}</StatusChip>
              </div>
              <p className="text-sm">{report.snapshot}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={!canAction}
                  onClick={() => action.mutate({ reportId: report.id, action: "triage" })}
                >
                  Triage
                </Button>
                <Button
                  size="sm"
                  disabled={!canAction}
                  onClick={() =>
                    action.mutate({
                      reportId: report.id,
                      action: "mute_social",
                      reason: "Mock mute social",
                    })
                  }
                >
                  Mute social
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canAction}
                  onClick={() => action.mutate({ reportId: report.id, action: "dismiss" })}
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={!canAction}
                  onClick={() => action.mutate({ reportId: report.id, action: "escalate" })}
                >
                  Escalate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canAction}
                  onClick={() =>
                    navigate({ to: "/moderation/$reportId", params: { reportId: report.id } })
                  }
                >
                  Hide / Ban…
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <h2 className="font-display font-semibold text-lg">
          Venue bans (reversal path) / รายชื่อแบน
        </h2>
        {bans.length === 0 ? (
          <EmptyState
            icon={<ShieldOff />}
            title="No active venue bans"
            description="Bans applied from a report's enforcement action appear here for reversal (SAFE-011)."
          />
        ) : (
          <ul className="space-y-2">
            {bans.map((ban) => (
              <li
                key={ban.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-sm"
              >
                <span>
                  <span className="font-mono">{ban.lineUserId}</span>
                  {ban.reason ? (
                    <span className="text-muted-foreground"> · {ban.reason}</span>
                  ) : null}
                  <span className="text-muted-foreground"> · banned {ban.bannedAt}</span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canAction}
                  onClick={() => unban.mutate(ban.id)}
                >
                  Unban / ยกเลิกแบน
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
